import { Container, Graphics, Text } from 'pixi.js';
import { registerResizableObject } from '../utils/pixi/registerResizableObject';
import { LoadingSpinnerView } from './LoadingSpinnerView';

type LoadingViewDependencies = {
  getParent: () => Container;
};

export class LoadingView extends Container {
  private static instance: LoadingView;

  private static dependencies: LoadingViewDependencies;

  private state: 'showing' | 'hidden' = 'hidden';

  private readonly overlay = new Graphics().props({
    parent: this,
    alpha: 0.8,
  });

  public static getInstance(): LoadingView {
    if (!this.instance) this.instance = new LoadingView();
    return this.instance;
  }

  private readonly spinner = new LoadingSpinnerView().props({
    parent: this,
  });

  private readonly descriptionText = new Text({
    text: '',
    style: {
      fontFamily: 'Baloo',
      fill: 'white',
      fontSize: 30,
    },
  }).props({
    anchor: 0.5,
    parent: this,
  });

  public static setDependencies(dependencies: LoadingViewDependencies): void {
    this.dependencies = dependencies;
  }

  public static getDependencies(): LoadingViewDependencies {
    if (!this.dependencies) throw new Error('LoadingView dependencies not set');
    return this.dependencies;
  }

  private constructor() {
    super();
    this.interactive = true;
    registerResizableObject(this);
  }

  public resize(width: number, height: number): void {
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height).fill('black');
    this.spinner.props({
      x: width * 0.5,
      y: height * 0.5,
    });
    this.descriptionText.props({
      x: width * 0.5,
      y: height * 0.5 + 100,
    });
  }

  public async show(textToShow?: string): Promise<void> {
    if (this.state === 'showing') return;
    this.state = 'showing';

    const { getParent } = LoadingView.getDependencies();

    getParent().addChild(this);

    this.removeTweens();
    this.alpha = 0;
    this.descriptionText.props({
      text: textToShow ?? '',
      visible: !!textToShow,
    });
    this.tweenTo({ alpha: 1 }, 200, 'sinOut', true);
  }

  public async hide(instant = false): Promise<void> {
    if (this.state === 'hidden') return;
    this.state = 'hidden';

    this.removeTweens();

    if (!instant) {
      this.alpha = 1;
      await this.tweenTo({ alpha: 0 }, 200, 'sinIn', true).asyncOnComplete();
    }

    this.parent?.removeChild(this);
  }
}
